import { z } from 'zod';
import type { ServiceViewScope } from '@/apis/membershipServices/usePrivateServiceView';
const uuid = z.uuid().regex(/^[0-9a-f-]{36}$/);
const id = uuid.refine((v) => v[14] === '7');
const time = z.iso.datetime({ offset: true });
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const chatImageSchema = z.strictObject({
  id,
  width: z.number().int().min(1).max(2048),
  height: z.number().int().min(1).max(2048),
  sizeBytes: z.number().int().min(1).max(MAX_IMAGE_BYTES),
});
export const imageSchema = chatImageSchema.extend({
  id,
  width: z.number().int().min(1).max(2048),
  height: z.number().int().min(1).max(2048),
  sizeBytes: z.number().int().min(1).max(MAX_IMAGE_BYTES),
  createdAt: time,
  sentByMe: z.boolean(),
});
export const pageSchema = z
  .strictObject({
    viewId: id,
    uploadAvailable: z.boolean(),
    images: z.array(imageSchema).max(20),
    nextCursor: id.nullable(),
    viewedAt: time,
    expiresAt: time,
  })
  .refine(
    (v) =>
      new Set(v.images.map((i) => i.id)).size === v.images.length &&
      (!v.nextCursor ||
        (v.images.length === 20 && v.images.at(-1)?.id === v.nextCursor)) &&
      Date.parse(v.viewedAt) < Date.parse(v.expiresAt) &&
      Date.parse(v.expiresAt) - Date.parse(v.viewedAt) <= 300000,
  );
export const prepareBodySchema = z.strictObject({
  viewId: id,
  clientRequestId: uuid,
  originalName: z.string().refine(
    (s) =>
      !!s.trim() &&
      Array.from(s).length <= 255 &&
      !Array.from(s).some((c) => {
        const n = c.codePointAt(0)!;
        return (
          n < 32 ||
          (n >= 127 && n <= 159) ||
          (n >= 0xd800 && n <= 0xdfff) ||
          c === '/' ||
          c === '\\'
        );
      }),
  ),
  contentType: z.literal('image/jpeg'),
  sizeBytes: z.number().int().min(1).max(MAX_IMAGE_BYTES),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export const uploadSchema = z.strictObject({
  uploadId: id,
  method: z.literal('PUT'),
  url: z.string().max(8192),
  headers: z.record(z.string(), z.string().max(8192)),
  urlExpiresAt: time,
  expiresAt: time,
});
export const completionSchema = z.strictObject({
  image: imageSchema,
  expiresAt: time,
});
export const downloadSchema = z.strictObject({
  imageId: id,
  url: z.string().max(8192),
  urlExpiresAt: time,
  expiresAt: time,
});
export type ImageMeta = z.infer<typeof imageSchema>;
export type ImagePage = z.infer<typeof pageSchema>;
export type PrepareBody = z.infer<typeof prepareBodySchema>;
export type Upload = z.infer<typeof uploadSchema>;
export type Download = z.infer<typeof downloadSchema>;
export interface ImageApi {
  scope: ServiceViewScope;
  list(signal: AbortSignal, cursor?: string): Promise<ImagePage>;
  prepare(body: PrepareBody, signal: AbortSignal): Promise<Upload>;
  complete(
    upload: Upload,
    signal: AbortSignal,
  ): Promise<z.infer<typeof completionSchema>>;
  read(image: ImageMeta, signal: AbortSignal): Promise<Download>;
}
export type PrivateImagePurpose = 'CHAT' | 'AFTERCARE';
export function signedUrl(
  value: string,
  imageId: string,
  upload: boolean,
  purpose: PrivateImagePurpose = 'CHAT',
) {
  const url = new URL(value);
  if (
    url.protocol !== 'https:' ||
    url.username ||
    url.password ||
    url.hash ||
    (url.port && url.port !== '443') ||
    !/^glow-private-[a-z0-9][a-z0-9-]*\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com(?:\.cn)?$/.test(
      url.hostname,
    ) ||
    !new RegExp(
      `^/(local|develop|main)/${purpose === 'AFTERCARE' ? (upload ? 'aftercare-upload' : 'aftercare') : upload ? 'chat-upload' : 'chat'}/[0-9]{4}/(0[1-9]|1[0-2])/${imageId}\\.jpg$`,
    ).test(url.pathname) ||
    !url.searchParams.get('X-Amz-Signature')
  )
    throw new Error('PRIVATE_IMAGE_RESPONSE_INVALID');
  return url;
}
export function signedHeaders(
  upload: Upload,
  body?: Pick<PrepareBody, 'sha256'>,
  purpose: PrivateImagePurpose = 'CHAT',
) {
  const allowed = new Set([
    'content-type',
    'cache-control',
    'x-amz-server-side-encryption',
    'x-amz-checksum-sha256',
    'x-amz-sdk-checksum-algorithm',
    'x-amz-meta-sha256',
    'x-amz-meta-purpose',
  ]);
  if (
    Object.keys(upload.headers).some((k) => !allowed.has(k)) ||
    upload.headers['content-type'] !== 'image/jpeg' ||
    upload.headers['x-amz-server-side-encryption'] !== 'AES256'
  )
    throw new Error('PRIVATE_IMAGE_RESPONSE_INVALID');
  const h = upload.headers;
  if (
    (h['cache-control'] !== undefined &&
      h['cache-control'] !== 'private,no-store') ||
    (h['x-amz-sdk-checksum-algorithm'] !== undefined &&
      h['x-amz-sdk-checksum-algorithm'] !== 'SHA256') ||
    (h['x-amz-meta-purpose'] !== undefined &&
      h['x-amz-meta-purpose'] !==
        (purpose === 'AFTERCARE'
          ? 'glow-private-aftercare-upload'
          : 'glow-private-chat-upload')) ||
    (body &&
      h['x-amz-meta-sha256'] !== undefined &&
      h['x-amz-meta-sha256'] !== body.sha256) ||
    (body &&
      h['x-amz-checksum-sha256'] !== undefined &&
      h['x-amz-checksum-sha256'] !==
        btoa(
          body.sha256
            .match(/../g)!
            .map((v) => String.fromCharCode(parseInt(v, 16)))
            .join(''),
        ))
  )
    throw new Error('PRIVATE_IMAGE_RESPONSE_INVALID');
}
